import client from 'core/axios';

import handleError from 'core/axios/handleError';

import { handleJSONResponse } from 'core/axios/handleResponse';

import RequestMethod from 'core/axios/RequestMethod';

/**
 * get paginated list of students for a given state program (or for all state programs)
 * @param {Object} params - query params
 * @param {Array[number]} params.programs - list of state program ids
 * @param {Boolean} params.use_planned_courses - flag to include planned courses
 * @param {Boolean} params.completed_ccmr - flag to include completed ccmr
 * @param {Array[number]} params.credentials - list of credential ids
 * @param {Array[string]} params.status - list of statuses
 * @returns {Promise} - list of students for a given state program
 */
export const listStateProgramStudents = ({
  version = 'v1',
  params = {},
  cancelToken = null,
}) =>
  client
    .request({
      url: `${version}/cp-programs/state-program-students/`,
      method: RequestMethod.GET,
      params,
      cancelToken,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * get counts of students for a given state program
 * @param {Object} params - query params
 * @param {Array[number]} params.programs - list of state program ids
 * @param {Boolean} params.use_planned_courses - flag to include planned courses
 * @param {Boolean} params.completed_ccmr - flag to include completed ccmr
 * @param {Array[number]} params.credentials - list of credential ids
 * @param {Array[string]} params.status - list of statuses
 * @returns {Promise} - counts of students for a given state program
 */
export const getStateProgramStudentCounts = ({ version = 'v1', params = {} }) =>
  client
    .request({
      url: `${version}/cp-programs/state-program-students/status-counts/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * get list of a student's state programs in progress or completed
 * @param {Object} params - query params
 * @param {Boolean} params.use_planned_courses - flag to include planned courses
 * @param {Number} params.student_id - student id
 * @returns {Promise} - list of a student's state programs
 */
export const listStudentStatePrograms = ({ version = 'v1', params = {} }) =>
  client
    .request({
      url: `${version}/cp-programs/student-state-programs/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * get counts of a student's state programs in progress or completed
 * @param {Object} params - query params
 * @param {Boolean} params.use_planned_courses - flag to include planned courses
 * @param {Number} params.student_id - student id
 * @returns {Promise} - counts of a student's state programs
 */
export const getStudentStateProgramCounts = ({ version = 'v1', params = {} }) =>
  client
    .request({
      url: `${version}/cp-programs/student-state-programs/status-counts/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * get list of state programs for a given state
 * @param {Object} params - query params
 * @param {String} params.state_abbreviation - state abbreviation (eg. "TX")
 * @returns {Promise} - list of state programs
 */
export const listStatePrograms = ({ version = 'v1', params = {} }) =>
  client
    .request({
      url: `${version}/cp-programs/state-programs/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * get context of state program students
 * @returns {Promise} - context of state program students
 */
export const getStateProgramStudentsContext = ({ version = 'v1' }) =>
  client
    .request({
      url: `${version}/cp-programs/state-program-students/context/`,
      method: RequestMethod.GET,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * get CSV report of state program students
 * @param {Object} params - query params
 * @param {Array[number]} params.programs - list of state program ids
 * @param {Boolean} params.use_planned_courses - flag to include planned courses
 * @param {Boolean} params.completed_ccmr - flag to include completed ccmr
 * @param {Array[number]} params.credentials - list of credential ids
 * @param {Array[string]} params.status - list of statuses
 * @returns {Promise} - CSV report of state program students
 */
export const getStateProgramsStudentsCSV = ({ version = 'v1', params = {} }) =>
  client
    .request({
      url: `${version}/cp-programs/state-program-students/csv/`,
      method: RequestMethod.POST,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * get students hash for state program students
 * @param {Object} params - query params
 * @param {Array[number]} params.programs - list of state program ids
 * @param {Boolean} params.use_planned_courses - flag to include planned courses
 * @param {Boolean} params.completed_ccmr - flag to include completed ccmr
 * @param {Array[number]} params.credentials - list of credential ids
 * @param {Array[string]} params.status - list of statuses
 * @returns {Promise} - student hash for state program students
 */
export const getStateProgramStudentsHash = ({ version = 'v1', params = {} }) =>
  client
    .request({
      url: `${version}/cp-programs/state-program-students/students-hash/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
